import CloseIcon from '@mui/icons-material/CloseOutlined';
import { Box, Button, Card, Divider, Drawer, FormHelperText, LinearProgress, Toolbar, Typography } from '@mui/material';
import { ErrorCode, FileRejection, useDropzone } from 'react-dropzone';
import { useTranslation } from 'react-i18next';
import { useMemo, useState } from 'react';
import { useAtomValue } from 'jotai';
import ConfirmDialog from '../../../common/components/ConfirmDialog';
import CopyToClipboard from '../../../common/components/CopyToClipboard';
import useOpenable from '../../../hooks/useOpenable';
import { useNavBlocker } from '../../../hooks/useNavBlocker';
import { orderAtom } from '../../state/state';
import { useProcessInvoice } from './useProcessInvoice';
import InvoiceCard from './InvoiceCard';

export interface UploadInvoiceDrawerProps {
  orderReferenceId: string;
  open: boolean;
  onClose: () => void;
}
const errorCodeWordingMap = new Map<ErrorCode, string>([
  [ErrorCode.TooManyFiles, 'only_one_file_is_supported'],
  [ErrorCode.FileTooLarge, 'file_too_large'],
]);

export default function UploadInvoiceDrawer({ orderReferenceId, open, onClose }: UploadInvoiceDrawerProps): React.JSX.Element {
  const { t } = useTranslation(['order', 'common', 'error']);
  const { processInvoice, processedInvoice, loading, reset } = useProcessInvoice({ orderReferenceId });
  const order = useAtomValue(orderAtom);
  // navBlocker is called to block page refresh when drawer is open and invoice was parsed
  useNavBlocker(!!processedInvoice);
  const { isOpen: isDialogOpen, open: openDialog, close: closeDialog } = useOpenable();
  const [fileErrorMessage, setFileErrorMessage] = useState<string>();

  const onDrop = async (acceptedFiles: File[], rejectedFiles: FileRejection[]): Promise<void> => {
    if (rejectedFiles.length) {
      const errorCode = rejectedFiles[0].errors[0].code as ErrorCode;
      setFileErrorMessage(t(`error:${errorCodeWordingMap.get(errorCode)}`, { size: '20 MB' }));
    } else {
      setFileErrorMessage(undefined);
      processInvoice(acceptedFiles[0]);
    }
  };

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    multiple: false,
    maxSize: 20971520, // 20MB in binary
    accept: {
      'image/png': ['.png'],
      'image/jpeg': ['.jpg', '.jpeg'],
      'application/pdf': ['.pdf'],
    },
  });

  const closeDrawer = (): void => {
    reset();
    onClose();
  };

  const handleCloseDrawer = (): void => {
    if (processedInvoice) {
      openDialog();
    } else {
      closeDrawer();
    }
  };

  const handleDialog = (isConfirmed: boolean): void => {
    if (isConfirmed) {
      closeDrawer();
    }
    closeDialog();
  };

  const invoicingEmailAddress = useMemo(() => {
    const buyerPrefix = order.contract.buying_company.invoice_prefix;
    const supplierPrefix = order.contract.supplying_company.invoice_prefix;

    return `invoices-${buyerPrefix}-${supplierPrefix}@${import.meta.env.REACT_APP_KHEOPS_INVOICING_DOMAIN}`;
  }, [order]);

  return (
    <Drawer
      anchor="bottom"
      open={open}
      onClose={handleCloseDrawer}
      slotProps={{
        paper: {
          sx: {
            backgroundColor: 'surface.main',
            overflowY: 'auto',
          },
        },
      }}
    >
      <ConfirmDialog
        title={t('order:abort_invoice_add_title')}
        subTitle={t('order:abort_invoice_add_description')}
        confirmLabel={t('common:confirm')}
        cancelLabel={t('common:cancel')}
        open={isDialogOpen}
        onClose={handleDialog}
      />
      <Toolbar
        variant="dense"
        sx={{
          backgroundColor: 'surfaceContainerLowest.main',
          py: 3,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          gap: 2,
          minHeight: 40,
          position: 'sticky',
          top: 0,
          zIndex: 1,
        }}
      >
        <Typography variant="displayMedium">
          {t('order:attach_an_invoice')}
        </Typography>
        <Button variant="text" onClick={handleCloseDrawer}>
          <CloseIcon />
        </Button>
      </Toolbar>
      <Box sx={{ pt: 3, display: 'flex', mx: 'auto', width: '100%' }}>
        {!loading && !processedInvoice && (
          <Card
            sx={{
              mx: {
                xs: 3,
                sm: 'auto',
              },
              width: {
                xs: '100%',
                sm: 600,
              },
              display: 'flex',
              flexDirection: 'column',
              gap: 3,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
                alignItems: 'center',
              }}
            >
              <Typography variant="titleLarge">
                {t('order:from_kheops')}
              </Typography>
              <Box
                {...getRootProps()}
                sx={{
                  p: 3,
                  border: '1px dashed',
                  borderColor: 'outlineVariant.main',
                  borderRadius: 4,
                  backgroundColor: 'surfaceContainerLow.main',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 2,
                  width: '100%',
                  boxSizing: 'border-box',
                }}
              >
                <input {...getInputProps()} />
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    textAlign: 'center',
                  }}
                >
                  <Typography variant="titleMedium">
                    {t('order:drag_and_drop_the_invoice_or_click_to_add_it')}
                  </Typography>
                  <Typography variant="bodySmall" color="secondary">{t('order:invoice_format_description')}</Typography>
                </Box>
                <Button variant="contained">
                  {t('order:browse_my_files')}
                </Button>
                {fileErrorMessage && (<FormHelperText error>{fileErrorMessage}</FormHelperText>)}
              </Box>
            </Box>
            <Divider sx={{ width: '100%' }}>
              <Typography variant="bodyLarge" align="center" sx={{ textTransform: 'capitalize' }}>
                {t('common:or')}
              </Typography>
            </Divider>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
                alignItems: 'center',
              }}
            >
              <Typography variant="titleLarge">
                {t('order:by_email')}
              </Typography>
              <Typography variant="bodyMedium">
                {t('order:email_invoice_description')}
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  border: '1px solid',
                  borderColor: 'divider',
                  backgroundColor: 'surfaceContainerLow.main',
                  alignItems: { xs: 'start', sm: 'center' },
                  mt: 0.5,
                  borderRadius: 3,
                  px: 3,
                  py: 2,
                  pb: 1,
                  pr: 1,
                  width: 'stretch',
                }}
              >
                <Typography variant="bodySmall">
                  {t('order:dedicated_email')}
                </Typography>
                <CopyToClipboard textToCopy={invoicingEmailAddress}>
                  <Typography variant="bodyMedium">
                    <a href={`mailto:${invoicingEmailAddress}`} style={{ color: 'inherit' }}>
                      {invoicingEmailAddress}
                    </a>
                  </Typography>
                </CopyToClipboard>
              </Box>

            </Box>
          </Card>
        )}
        {loading && (
          <Card
            sx={{
              mx: {
                xs: 3,
                sm: 'auto',
              },
              height: 480,
              width: {
                xs: '100%',
                sm: 600,
              },
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 1,
              justifyContent: 'center',
            }}
          >
            <Typography variant="titleMedium">{t('common:please_wait')}...</Typography>
            <LinearProgress sx={{ width: '80%' }} />
          </Card>
        )}
        {processedInvoice && (
          <InvoiceCard
            closeDrawer={closeDrawer}
            invoiceData={processedInvoice}
            onClose={handleCloseDrawer}
          />
        )}
      </Box>
    </Drawer>
  );
}
